import { useEffect, useMemo, useState } from "react";
import {
    expandFilteredNodes,
    filterTree,
    levelTreeNodes,
    normalizeTreeResponse,
    preserveToggleState,
} from "./treeUtils";

export default function DBSearchableTree({
    data,
    decorators,
    searchTerm,
    notfoundMessage,
    selectedNodeHandle,
    selectedNode,
    direction,
    functions,
}) {
    const leveledData = useMemo(
        () => ({
            name: "Root",
            children: levelTreeNodes(normalizeTreeResponse(data)),
        }),
        [data]
    );

    return (
        <DBSearchableTreeFunctions
            data={leveledData}
            searchTerm={searchTerm}
            notfoundMessage={notfoundMessage}
            decorators={decorators}
            selectedNodeHandle={selectedNodeHandle}
            selectedNode={selectedNode}
            direction={direction}
            functions={functions}
        />
    );
}

function SimpleTree({ nodes, onToggle, decorators, direction, isRoot }) {
    if (!nodes?.length) return null;
    return (
        <ul
            style={
                isRoot
                    ? undefined
                    : direction === "rtl"
                    ? { paddingRight: "14px" }
                    : { paddingLeft: "14px" }
            }
        >
            {nodes.map((node) => (
                <li key={node.id}>
                    <decorators.Container
                        node={node}
                        onClick={() => onToggle(node, !node.toggled)}
                        decorators={decorators}
                    />
                    {node.toggled ? (
                        <SimpleTree
                            nodes={normalizeTreeResponse(node.children)}
                            onToggle={onToggle}
                            decorators={decorators}
                            direction={direction}
                        />
                    ) : null}
                </li>
            ))}
        </ul>
    );
}

function DBSearchableTreeFunctions({
    data,
    decorators,
    searchTerm,
    notfoundMessage,
    selectedNodeHandle,
    selectedNode: externalSelectedNode,
    direction,
    functions,
}) {
    const [dataState, setDataState] = useState(data);
    const [cursor, setCursor] = useState(false);

    useEffect(() => {
        setDataState((prev) => ({
            name: "Root",
            children: preserveToggleState(data.children, prev.children),
        }));
    }, [data]);

    const searchResults = useMemo(() => {
        const term = String(searchTerm ?? "").trim();
        if (!term) return dataState;
        return expandFilteredNodes(filterTree(dataState, term), term);
    }, [dataState, searchTerm]);

    const onToggle = (node, toggled) => {
        if (cursor) {
            cursor.active = false;
        }
        node.active = true;
        if (node.children) {
            node.toggled = toggled;
        }
        setCursor(node);
        setDataState({ ...dataState });
    };

    const [selectedNode, setSelectedNode] = useState();

    useEffect(() => {
        setSelectedNode(externalSelectedNode || undefined);
    }, [externalSelectedNode?.id]);

    useEffect(() => {
        if (selectedNodeHandle && selectedNode) {
            selectedNodeHandle(selectedNode);
        }
    }, [selectedNode]);

    const visibleChildren = normalizeTreeResponse(searchResults?.children);

    return (
        <>
            {visibleChildren.length > 0 ? (
                <SimpleTree
                    nodes={visibleChildren}
                    onToggle={onToggle}
                    isRoot
                    decorators={{
                        Container: decorators,
                        onSelect: (node) => {
                            node && setSelectedNode(node);
                        },
                        functions,
                        selectedNode,
                    }}
                    direction={direction}
                />
            ) : (
                notfoundMessage
            )}
        </>
    );
}
